import React, {useEffect, useState} from 'react';
import {CircularProgress} from '@mui/material';
import MyChip from 'ui-component/extended/Chip';
import {callbackNetworkResult} from 'api/customer/files.api';
import {refreshDocsCall, sendReqForApi} from 'api/customer/documents.api';
import {resolveExternalSoftwareDocType} from 'utils/externalSoftware';

export type DocumentApiSendChipProps = {
    document: any;
    docType?: number;
    labelSent: string;
    labelNotSent: string;
    isSent: (doc: any) => boolean;
    refresher?: {mutateAsync?: (args: any) => Promise<any>; mutate?: (args: any) => void};
    variant?: 'outlined' | 'filled';
    size?: 'small' | 'medium';
    chipcolor?: string;
    color?: 'primary' | 'secondary' | 'default';
};

const pickRefreshedDoc = (refreshed: any, current: any) => {
    const list = Array.isArray(refreshed) ? refreshed : refreshed != null ? [refreshed] : [];
    return list.find((d) => Number(d?.id) === Number(current?.id)) ?? list[0] ?? current;
};

/** Send chip: spinner while SEND_API_REQ + document refresh, then the fetched send status. */
const DocumentApiSendChip = ({
    document,
    docType,
    labelSent,
    labelNotSent,
    isSent,
    refresher,
    variant,
    size,
    chipcolor,
    color = 'primary'
}: DocumentApiSendChipProps) => {
    const [sending, setSending] = useState(false);
    const [liveDoc, setLiveDoc] = useState(document);

    useEffect(() => {
        setLiveDoc(document);
    }, [document?.id]);

    if (!liveDoc?.id) return null;
    const type = docType ?? resolveExternalSoftwareDocType(liveDoc);
    if (type == null || type < 0) return null;
    const sent = isSent(liveDoc);

    return (
        <MyChip
            color={color}
            chipcolor={chipcolor}
            variant={variant}
            size={size}
            disabled={sending}
            onClick={() => {
                if (sending) return;
                setSending(true);
                callbackNetworkResult(async () => {
                    await sendReqForApi(liveDoc.id, type);
                    if (typeof refresher?.mutateAsync === 'function') {
                        return await refresher.mutateAsync({
                            ids: [liveDoc.id],
                            types: [type],
                            full: true
                        });
                    }
                    return await refreshDocsCall([liveDoc.id], [type], false, true);
                }, (refreshed) => {
                    setLiveDoc(pickRefreshedDoc(refreshed, liveDoc));
                }, setSending);
            }}
            label={sending ? <CircularProgress size={16} color='inherit' /> : (sent ? labelSent : labelNotSent)}
            sx={{cursor: sending ? 'default' : 'pointer', minWidth: 72, justifyContent: 'center'}}
        />
    );
};

export default DocumentApiSendChip;
